import React, { useState } from "react";
import { useAuth } from "../../auth/useAuth.js";
import { bidsApi } from "../../api/auctiongate.js";

/**
 * Three mini-forms bundled:
 *   - Save bid (plugin `saveBid`)
 *   - Send active bid (plugin `sendActiveBids`)
 *   - Check user purchased (plugin `checkUserPurchased`)
 */
export default function BidTools() {
  const { user } = useAuth();
  const userId = user?.id ?? "";

  return (
    <div className="card">
      <div className="card__title">
        Bid tools
        <span className="badge">save / active / check</span>
      </div>
      <div className="grid grid-2">
        <SaveBidForm userId={userId} />
        <SendActiveBidForm />
        <CheckPurchasedForm userId={userId} />
      </div>
    </div>
  );
}

function SaveBidForm({ userId }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const onSubmit = async (e) => {
    e.preventDefault();
    const f = Object.fromEntries(new FormData(e.currentTarget).entries());
    setBusy(true);
    setMsg(null);
    try {
      const r = await bidsApi.save({
        user_subbiders_id: userId,
        auction: f.auction,
        bet: f.bet,
        lot_number: f.lot_number,
      });
      setMsg(r?.message || "Saved.");
    } catch (err) {
      setMsg(err?.message || "Failed.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={onSubmit} className="stack" style={box}>
      <b style={sub}>Save bid</b>
      <select name="auction" defaultValue="copart" style={input}>
        <option>copart</option>
        <option>iaai</option>
        <option>copart_canada</option>
        <option>iaai_canada</option>
      </select>
      <input name="lot_number" placeholder="Lot number" required style={input} />
      <input name="bet" type="number" placeholder="Bet (USD)" required style={input} />
      <button className="btn btn-primary" disabled={busy || !userId}>
        {busy ? <span className="loader" /> : "Save"}
      </button>
      {msg && <div className="small muted">{String(msg)}</div>}
    </form>
  );
}

function SendActiveBidForm() {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const onSubmit = async (e) => {
    e.preventDefault();
    const f = Object.fromEntries(new FormData(e.currentTarget).entries());
    setBusy(true);
    setMsg(null);
    try {
      const r = await bidsApi.active({ lots: [{ lot_number: f.lot_number, amount: Number(f.amount), auction_id: f.auction_id }] });
      setMsg(r?.message || "Sent.");
    } catch (err) {
      setMsg(err?.message || "Failed.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={onSubmit} className="stack" style={box}>
      <b style={sub}>Send active bid</b>
      <select name="auction_id" defaultValue="2" style={input}>
        <option value="1">IAAI (1)</option>
        <option value="2">Copart (2)</option>
        <option value="11">IAAI Canada (11)</option>
        <option value="14">Copart Canada (14)</option>
      </select>
      <input name="lot_number" placeholder="Lot number" required style={input} />
      <input name="amount" type="number" placeholder="Amount (USD)" required style={input} />
      <button className="btn btn-accent" disabled={busy}>
        {busy ? <span className="loader" /> : "Send"}
      </button>
      {msg && <div className="small muted">{String(msg)}</div>}
    </form>
  );
}

function CheckPurchasedForm({ userId }) {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const check = async () => {
    setBusy(true);
    setErr(null);
    setData(null);
    try {
      setData(await bidsApi.checkPurchased(userId));
    } catch (e) {
      setErr(e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };
  const arr = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
  return (
    <div className="stack" style={box}>
      <b style={sub}>Purchased lots (by user)</b>
      <button className="btn btn-ghost" onClick={check} disabled={busy || !userId}>
        {busy ? <span className="loader" /> : "Fetch"}
      </button>
      {err && <div className="login-error small">{err}</div>}
      {data && (
        <div className="small">
          {arr.length} entries.
          {arr.slice(0, 3).map((it, i) => (
            <div key={i} className="muted">
              · lot {it.lot_number || it.id}
              {it.vin && <> · VIN {it.vin}</>}
            </div>
          ))}
          {arr.length > 3 && <div className="muted">…{arr.length - 3} more</div>}
        </div>
      )}
    </div>
  );
}

const box = {
  padding: 12,
  border: "1px solid var(--color-border)",
  borderRadius: 10,
  background: "var(--color-bg-card-elevated)",
};
const sub = { fontSize: 13, color: "#0f172a", marginBottom: 4 };
const input = {
  padding: "7px 10px",
  border: "1px solid var(--color-border-input)",
  borderRadius: 8,
  fontSize: 13,
  background: "#fff",
};
